import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { setSignupRole, setSignupSport } from '../../redux/slices/signupSlice';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { athleteSignupSchema, coachSignupSchema } from '../../validations/signupValidation';
import useRegister from '../../hooks/auth/useRegister';
import useRegisterCoachRequest from '../../hooks/auth/useRegisterCoachRequest';
import BackButton from '../BackButton/BackButton';
import ToastPopup from '../ToastPopup/ToastPopup';
import UploadcareModalUploader from '../UploadcareModalUploader/UploadcareModalUploader';
import useToast from '../../hooks/ui/useToast';
import styles from '../LoginForm/LoginForm.module.css';
import globalButtonStyles from '../../styles/ButtonGradient.module.css';

const inputFields = [
  { type: 'text', placeholder: 'First Name', name: 'firstName' },
  { type: 'text', placeholder: 'Last Name', name: 'lastName' },
  { type: 'email', placeholder: 'Email', name: 'email' }
];

const SignupForm = () => {
  const { role, sport } = useSelector((state) => state.signup);
  const { registerAthlete } = useRegister();
  const { registerCoach } = useRegisterCoachRequest();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  useEffect(() => {
    const roleFromUrl = searchParams.get('role');
    const sportFromUrl = searchParams.get('sport');
  
    if (roleFromUrl) dispatch(setSignupRole(roleFromUrl));
    if (sportFromUrl) dispatch(setSignupSport(sportFromUrl));
  }, [searchParams, dispatch]);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    organization: '',
    profilePhoto: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [showUploader, setShowUploader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { toast, showToast, closeToast } = useToast(3000);

  const validateFormData = async () => {
    try {
      if (role === 'athlete') {
        await athleteSignupSchema.validate(formData, { abortEarly: false });
      } else if (role === 'coach') {
        await coachSignupSchema.validate(formData, { abortEarly: false });
      }
      setFormErrors({});
      return null;
    } catch (err) {
      const errors = {};
      if (err.inner && err.inner.length > 0) {
        err.inner.forEach((e) => {
          errors[e.path] = e.message;
        });
      } else {
        errors.general = err.message;
      }
      setFormErrors(errors);
      return err.message;
    }
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    try {
      if (role === 'athlete') {
        await athleteSignupSchema.validateAt(name, { ...formData, [name]: value });
      } else if (role === 'coach') {
        await coachSignupSchema.validateAt(name, { ...formData, [name]: value });
      }
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    } catch (err) {
      setFormErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      organization: '',
      profilePhoto: ''
    });
    setFormErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validationError = await validateFormData();
    if (validationError) {
      showToast(validationError, 'error');
      setLoading(false);
      return;
    }

    try {
      if (role === 'athlete') {
        await registerAthlete({
          ...formData,
          sports: [sport],
          profilePicture: formData.profilePhoto
        });
        showToast('Signup successful! Welcome aboard. 🎉', 'success', resetForm);
      } else if (role === 'coach') {
        await registerCoach({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          organization: formData.organization || '',
          profilePicture: formData.profilePhoto
        });
        showToast('Thanks! Your request is submitted. You’ll receive an invite email once approved.', 'success', resetForm);
      }
    } catch (error) {
      console.error(error);
      showToast('There was an issue submitting your registration. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.wrapper}>
      <BackButton />
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.iconCircle}>
          {formData.profilePhoto ? (
            <img
              src={formData.profilePhoto}
              alt="Profile preview"
              className={styles.profilePreview}
              onClick={() => setShowUploader(true)}
              style={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                objectFit: 'cover',
                cursor: 'pointer',
              }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setShowUploader(true)}
              className={styles.addPhotoBtn}
            >
              Add Photo
            </button>
          )}
          <UploadcareModalUploader
            isOpen={showUploader}
            onClose={() => setShowUploader(false)}
            onUpload={(url) => setFormData({ ...formData, profilePhoto: url })}
          />
        </div>

        {inputFields.map((field) => (
          <div key={field.name} className={styles.inputWrapper}>
            <input
              key={field.name}
              type={field.type}
              placeholder={field.placeholder}
              name={field.name}
              className={styles.input}
              value={formData[field.name]}
              onChange={handleChange}
              required
            />
            {formErrors[field.name] && (
              <div className={styles.fieldError}>{formErrors[field.name]}</div>
            )}
          </div>
        ))}

        {role === 'coach' && (
          <div className={styles.inputWrapper}>
            <input
              type="text"
              placeholder="Organization"
              name="organization"
              className={styles.input}
              value={formData.organization || ''}
              onChange={handleChange}
            />
            {formErrors.organization && (
              <div className={styles.fieldError}>{formErrors.organization}</div>
            )}
          </div>
        )}

        {role === 'athlete' && (
          <>
            <div className={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                name="password"
                className={styles.input}
                value={formData.password}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label="Toggle password visibility"
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>

            {!formErrors.password && !formData.password && (
              <div className={styles.passwordHint}>
                Must be at least 8 characters and include an uppercase letter, lowercase letter, number, and special character.
              </div>
            )}
            {formErrors.password && (
              <div className={styles.fieldError}>{formErrors.password}</div>
            )}

            <div className={styles.passwordWrapper}>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                name="confirmPassword"
                className={styles.input}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                aria-label="Toggle confirm password visibility"
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {formErrors.confirmPassword && (
              <div className={styles.fieldError}>{formErrors.confirmPassword}</div>
            )}
          </>
        )}

        <button
          type="submit"
          className={`${globalButtonStyles.button} ${styles.loginBtn}`}
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Sign Up'}
        </button>
      </form>

      <ToastPopup
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={closeToast}
      />
    </div>
  );
};

export default SignupForm;